import {
  DEFAULT_ATTRIBUTES,
  getAttributeIDByName,
  getAttributeNameByID,
  getTypeByID,
  getTypedArrayByID
} from "_src/engine/utility/attributes";
import arrayTools from "_src/engine/utility/arrays";

export function readChunk(bufferSource, decoder = null) {

  const result = {
    "type"       : -1,
    "status"     : -1,
    "indices"    : null,
    "a_position" : null,
    "a_normal"   : null,
    "a_uv"       : null,
    "a_color"    : null
  };

  const uint8Data = new Uint8Array(bufferSource);

  let byteOffset = 0;

  // NOTE: https://confluence.artec-group.com:4432/pages/viewpage.action?pageId=39419976
  const compressionFormatSize = 4;
  const structureVersionSize = 2;
  const attrsCountOffset = 2;

  byteOffset += compressionFormatSize + structureVersionSize;

  const uint16AttrData = new Uint16Array(uint8Data.buffer, byteOffset, 1);
  const attrsCounter = uint16AttrData[0];

  byteOffset += attrsCountOffset;

  // metadata
  const metadataUint32Size = 3 * attrsCounter;
  const metadata = new Uint32Array(uint8Data.buffer, byteOffset, metadataUint32Size);

  byteOffset += metadataUint32Size * Uint32Array.BYTES_PER_ELEMENT;

  for (let i = 0; i < metadata.length; i += 3) {

    const id = metadata[i];

    const byteSize = metadata[i + 1];
    const compByteSize = metadata[i + 2];

    const attributeName = getAttributeNameByID(id);

    if (byteSize) {

      const compAttribute = new Uint8Array(uint8Data.buffer, byteOffset, compByteSize);
      const uint8Attribute = decoder ? decoder.decompress(compAttribute) : compAttribute.slice();
      byteOffset += compByteSize;

      let bufferType = getTypeByID(id);
      const typedArray = getTypedArrayByID(id);
      let bufferData = new typedArray(uint8Attribute.buffer);

      if (attributeName === "indices") {
        [bufferType, bufferData] = fixIndicesBuffer(bufferType, bufferData);
      }

      result[attributeName] = {
        type   : bufferType,
        buffer : bufferData,
      };

    }

  }

  return result;

}

export function writeChunk(data, encoderType = "pln ", encoder = null) {

  const result = {
    "type"        : -1,
    "status"      : -1,
    "arrayBuffer" : null
  };

  const compressedBuffers = {};

  // NOTE: https://confluence.artec-group.com:4432/pages/viewpage.action?pageId=39419976
  const encoderNameByteSize = 4;
  const versionByteSize = 2;
  const attrsNumByteSize = 2;
  const attributeMetaByteSize = 12;
  const attributeMetaSize = attributeMetaByteSize / Uint32Array.BYTES_PER_ELEMENT

  const compressionLevel = data.level || 5;

  let numAttributes = 0;
  let metadataByteSize = 8;
  let buffersByteSize = 0;

  for (let i = 0; i < DEFAULT_ATTRIBUTES.length; ++i) {

    const attrName = DEFAULT_ATTRIBUTES[i];
    const attributeBuffer = data[attrName];
    if (!attributeBuffer) {
      continue;
    }

    // we need this to send buffers back to the main thread
    result[attrName] = attributeBuffer;

    ++numAttributes;
    metadataByteSize += attributeMetaByteSize;

    const uint8Buffer = new Uint8Array(attributeBuffer.buffer);
    const compressedBuffer = encoder ? encoder.compress(uint8Buffer, compressionLevel) : uint8Buffer.slice();
    buffersByteSize += compressedBuffer.buffer.byteLength;
    compressedBuffers[attrName] = compressedBuffer;

  }

  const packedData = new Uint8Array(metadataByteSize + buffersByteSize);

  let offset = metadataByteSize;
  let metaOffset = 0;

  const dataType = new Uint8Array(packedData.buffer, metaOffset, encoderNameByteSize);
  dataType[0] = encoderType.charCodeAt(0);
  dataType[1] = encoderType.charCodeAt(1);
  dataType[2] = encoderType.charCodeAt(2);
  dataType[3] = encoderType.charCodeAt(3);
  metaOffset += encoderNameByteSize;

  const version = new Uint16Array(packedData.buffer, metaOffset, 1);
  version[0] = 1;
  metaOffset += versionByteSize;

  const numAttrsBuff = new Uint16Array(packedData.buffer, metaOffset, 1);
  numAttrsBuff[0] = numAttributes;
  metaOffset += attrsNumByteSize;

  for (let i = 0; i < DEFAULT_ATTRIBUTES.length; ++i) {

    const attrName = DEFAULT_ATTRIBUTES[i];
    let compressedBuffer = compressedBuffers[attrName];
    if (!compressedBuffer) {
      continue;
    }

    const byteSizeCompressed = compressedBuffer.buffer.byteLength;
    const byteSize = result[attrName].buffer.byteLength;

    const attributeBuffer = data[attrName];
    const id = getAttributeIDByName(attrName, attributeBuffer);

    packedData.set(compressedBuffer, offset);
    fillAttributeMetaData(packedData.buffer, metaOffset, byteSize, byteSizeCompressed, id, attributeMetaSize);
    offset += byteSizeCompressed;
    metaOffset += attributeMetaByteSize;

  }

  result["arrayBuffer"] = packedData;

  return result;

}

function fillAttributeMetaData(buffer, offset, byteSize, byteSizeCompressed, id, attributeMetaSize) {
  const descrition = new Uint32Array(buffer, offset, attributeMetaSize);
  descrition[0] = id;
  descrition[1] = byteSize;
  descrition[2] = byteSizeCompressed;
}

function fixIndicesBuffer(type, data) {
  const maxVal = data.reduce((a, b) => { return Math.max(a, b) });
  const realType = arrayTools.uintArrayTypeByMaxValue(maxVal);
  if (data instanceof realType) {
    return [type, data];
  } else {
    const castedData = arrayTools.createUintArrayByValueData(maxVal, data);
    const castedType = arrayTools.uintArrayType(castedData);
    return [castedType, castedData];
  }
}
